import { useEffect, useState } from 'react'
import { Alert, Form, Modal, Select, Space, Typography } from 'antd'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import type { OperationResult } from '@/shared/api/operationResult'
import { ExpandableHint } from '@/shared/ui/ExpandableHint'
import { OperationResultAlert } from '@/shared/ui/OperationResultAlert'
import { assignEquipment, unassignEquipment, type Equipment } from './equipmentApi'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import {
  ASSIGNABLE_OWNER_TYPES,
  isAssignableOwnerType,
  useOwnerCandidates,
  type EquipmentOwnerType,
} from './ownerApi'

interface EquipmentOwnerModalProps {
  open: boolean
  equipment: Equipment | null
  onClose: () => void
}

interface FormValues {
  ownerType: EquipmentOwnerType
  ownerId: string
}

/**
 * Jihozning egasini tayinlash va o'zgartirish (XSB-003).
 *
 * <h2>⚠️ "O'zgartirish" IKKI SO'ROV — va bu yashirilmaydi</h2>
 * Backendda `assign` allaqachon biriktirilgan jihozni RAD ETADI
 * (`EquipmentServiceImpl.assign` → `AlreadyAssigned`), chunki holat mashinasi
 * `ASSIGNED -> ASSIGNED` o'tishini bilmaydi. Ya'ni egani almashtirish
 * `unassign` + `assign` dan iborat va bu ATOMAR EMAS.
 *
 * Ikkinchi qadam yiqilsa jihoz OMBORDA qoladi — eski egasida ham, yangisida
 * ham emas. Bu holat ekranda AYNAN shunday aytiladi (`PARTIAL`, sariq):
 * "jihoz egasidan olindi, yangisiga biriktirilmadi". Uni "xato" deb
 * ko'rsatish operatorni qayta bosishga undardi (birinchi qadam esa
 * allaqachon o'tgan), "bajarildi" deb ko'rsatish esa YOLG'ON bo'lardi —
 * inventar hisobi jimgina noto'g'ri bo'lib qolardi.
 *
 * ⚠️ Shu sababdan almashtirish tasdiqlashsiz bajarilmaydi: forma ochilganda
 * joriy ega ko'rinib turadi va matn ikki qadamli ekanini aytadi.
 *
 * <h2>⚠️ Nega tanlagich, xom UUID emas</h2>
 * `ownerApi.ts` sarlavhasida. Qisqasi: xom id maydoni mavjud bo'lmagan egaga
 * biriktirishga yo'l ochardi va inventar hisobi o'z-o'zidan yolg'onga
 * aylanardi. `OTHER` turida ro'yxat yo'q, shuning uchun u biriktirishda
 * taklif qilinmaydi — operator ko'rmaydigan id ni so'rash ma'nosiz.
 */
export function EquipmentOwnerModal({ open, equipment, onClose }: EquipmentOwnerModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()

  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<OperationResult | null>(null)
  const [search, setSearch] = useState('')

  /*
    ⚠️ BOSHLANG'ICH QIYMAT `initialValues` DA, `useEffect` + `setFieldsValue`
    DA EMAS — va bu o'lchangan nuqson edi.

    `destroyOnHidden` + `preserve={false}` bilan forma har ochilishda
    QAYTADAN quriladi. `useEffect` esa oyna ochilgan renderda ishga tushadi,
    ya'ni maydonlar hali `form` ga ulanmagan bo'lishi mumkin —
    `setFieldsValue` jimgina bo'shlikka yozardi. Brauzerda aynan shunday
    ko'rindi: oyna tahrirlash oynasidan ochilganda «Egasining turi»
    tanlagichi BO'SH turardi, ro'yxat esa `SUBSCRIBER` bo'yicha kelardi —
    ekran va so'rov bir-biriga zid edi.

    Joriy tur oldindan tanlanadi: egani almashtirish odatda BIR XIL turda
    bo'ladi (abonentdan abonentga).
  */
  const currentType = equipment?.ownerType ?? null
  const initialType: EquipmentOwnerType = isAssignableOwnerType(currentType)
    ? currentType
    : 'SUBSCRIBER'

  const ownerType: EquipmentOwnerType = Form.useWatch('ownerType', form) ?? initialType
  const candidates = useOwnerCandidates(ownerType, search, open)

  const isReassign = equipment?.state === 'ASSIGNED'

  // Oyna yopilib qayta ochilganda eski xato va qidiruv so'zi qolmasin
  useEffect(() => {
    if (!open) return
    setResult(null)
    setSearch('')
  }, [open, equipment])

  const close = () => {
    setResult(null)
    onClose()
  }

  const submit = (values: FormValues) => {
    if (!equipment) return
    setBusy(true)
    setResult(null)
    void (async () => {
      try {
        if (isReassign) {
          await unassignEquipment(equipment.id)
          try {
            await assignEquipment(equipment.id, values.ownerType, values.ownerId.trim())
          } catch (error) {
            // ⚠️ Birinchi qadam O'TGAN — jihoz omborda. Buni yashirmaymiz.
            void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all })
            setResult({
              status: 'PARTIAL',
              message: t('inventory.ownerReassignPartial'),
              detail: translateError(error, t),
            })
            return
          }
        } else {
          await assignEquipment(equipment.id, values.ownerType, values.ownerId.trim())
        }
        void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all })
        close()
      } catch (error) {
        setResult({ status: 'FAILED', message: translateError(error, t) })
      } finally {
        setBusy(false)
      }
    })()
  }

  return (
    <Modal
      open={open}
      title={isReassign ? t('inventory.ownerChangeTitle') : t('inventory.ownerAssignTitle')}
      onCancel={close}
      onOk={() => submitOnValid(form, submit)}
      confirmLoading={busy}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      destroyOnHidden
      width={520}
    >
      {result ? (
        <div style={{ marginBottom: 16 }}>
          <OperationResultAlert partialNote={false} result={result} />
        </div>
      ) : null}

      {equipment ? (
        <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
          {t('inventory.ownerSubject', { serial: equipment.serialNumber })}
        </Typography.Paragraph>
      ) : null}

      {/* Almashtirish ikki qadamli — operator buni OLDINDAN bilsin, keyin emas */}
      {isReassign ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('inventory.ownerChangeWarnTitle')}
          description={<ExpandableHint text={t('inventory.ownerChangeWarnBody')} fontSize={13} />}
        />
      ) : null}

      <Form<FormValues>
        form={form}
        layout="vertical"
        preserve={false}
        disabled={busy}
        /*
          ⚠️ `ownerId` ATAYLAB BO'SH, joriy ega bilan to'ldirilmaydi. Aks holda
          "Saqlash" bosish jihozni O'SHA egadan olib, O'SHA egaga qaytadan
          biriktirardi — ya'ni ikkita bekorchi audit yozuvi va oradagi qisqa
          "omborda" oynasi. Almashtirish har doim YANGI tanlovni talab qiladi.
        */
        initialValues={{ ownerType: initialType, ownerId: '' }}
      >
        <Form.Item
          name="ownerType"
          label={t('inventory.ownerTypeLabel')}
          rules={[{ required: true, message: t('inventory.ownerTypeRequired') }]}
        >
          <Select<EquipmentOwnerType>
            options={ASSIGNABLE_OWNER_TYPES.map((value) => ({
              value,
              label: t(`equipmentOwnerType.${value}`),
            }))}
            onChange={() => {
              // Tur almashsa oldingi tanlov MA'NOSIZ bo'ladi — abonent id si
              // agentlar ro'yxatida yo'q, lekin maydonda qolib ketardi
              form.setFieldValue('ownerId', '')
              setSearch('')
            }}
          />
        </Form.Item>

        <Form.Item
          name="ownerId"
          label={t('inventory.ownerPickLabel')}
          extra={t('inventory.ownerPickHint')}
          rules={[{ required: true, message: t('inventory.ownerPickRequired') }]}
        >
          <Select
            showSearch
            filterOption={false}
            onSearch={setSearch}
            loading={candidates.loading}
            disabled={!candidates.allowed}
            placeholder={t('inventory.ownerPickPlaceholder')}
            notFoundContent={
              !candidates.allowed
                ? t('inventory.ownerNoPermission')
                : candidates.needsQuery
                  ? t('inventory.ownerTypeToSearch')
                  : candidates.loading
                    ? t('common.loading')
                    : t('inventory.ownerNothingFound')
            }
            options={candidates.options.map((o) => ({
              value: o.id,
              label: o.hint ? `${o.label} · ${o.hint}` : o.label,
            }))}
          />
        </Form.Item>

        {!candidates.allowed ? (
          <Space direction="vertical" size={0}>
            <Typography.Text type="warning">{t('inventory.ownerNoPermission')}</Typography.Text>
          </Space>
        ) : null}
      </Form>
    </Modal>
  )
}
